import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axiosApiInstance from '@/api/shared/axios-instance';
import URL_FACTORY from '@/helpers/url-helper';
import { mapToEvents, mapToH2hFixtures, mapToStats } from '../mappers/MapToAlineacionesProbables';
import { LEAGUES } from '@/constants/leagues';
import { currentSeasonStats } from '@/constants/site';
import { FANTASY } from '@/helpers/biwenger-helper';

export const PartidoAlineacionesContext = createContext(null);

export const usePartidoAlineaciones = () => {
    const context = useContext(PartidoAlineacionesContext);
    if (!context) {
        throw new Error('usePartidoAlineaciones debe ser usado dentro de un PartidoAlineacionesProvider');
    }
    return context;
};

export const PartidoAlineacionesProvider = ({
    children,
    data,
    handleOpenJugador,
    confirmedFantasy,
    confirmedFantasyMode,
}) => {
    const [variant, setVariant] = useState('vertical');
    const [h2h, setH2h] = useState(null);
    const [prediction, setPrediction] = useState(null);
    const [events, setEvents] = useState(null);
    const [estadisticasPartido, setEstadisticasPartido] = useState(null);
    const [estadisticasPartidoSeleccionada, setEstadisticasPartidoSeleccionada] = useState(null);
    const [stats, setStats] = useState(null);
    const [editorLineups, setEditorLineups] = useState(null);
    const [selectedEditorLineup, setSelectedEditorLineup] = useState(null);

    // Datos derivados
    const mode = data?.mode;
    const home = data?.home;
    const away = data?.away;
    const { id, date, league, season } = data?.fixture ?? {};

    // Función para agregar estadísticas a los jugadores
    const addEstadisticasPartido = (estadisticas) => {
        const statsMap = {};

        estadisticas.forEach((modo) => {
            Object.entries(modo.estadisticas).forEach(([playerId, stat]) => {
                if (!statsMap[playerId]) {
                    statsMap[playerId] = [];
                }
                statsMap[playerId].push({
                    mode: modo.mode,
                    subMode: modo.subMode,
                    estadistica: stat,
                });
            });
        });

        const applyStatsToLineup = (lineup) => {
            lineup?.forEach((player) => {
                player.estadisticas = statsMap[player.id] || [];
                player.lineupMode = data?.mode;
            });
        };

        applyStatsToLineup(data?.home?.lineup);
        applyStatsToLineup(data?.away?.lineup);
    };

    // Fetch stats del partido (comparación equipos)
    useEffect(() => {
        const homeId = home?.id;
        const awayId = away?.id;
        if (homeId > 0 && awayId > 0) {
            const payload = {
                homeId,
                awayId,
                league,
                season: currentSeasonStats,
            };

            const url = URL_FACTORY.ObtenerStatsPartido;

            axiosApiInstance
                .post(url, payload)
                .then((res) => {
                    setStats(mapToStats(res.data?.home, res.data?.away));
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    }, [home?.id, away?.id, league]);

    // Fetch H2H
    useEffect(() => {
        if (!id) return;
        const url = URL_FACTORY.ObtenerHistoricoDePartidos(id, 20);
        axiosApiInstance.get(url).then((res) => {
            const fixtures = mapToH2hFixtures(res.data?.h2h);
            setH2h(fixtures);
        });
    }, [id]);

    // Fetch predicción del tiempo
    useEffect(() => {
        if (!id || !date || league !== LEAGUES.LA_LIGA || mode !== 'predict') return;

        const now = new Date();
        const inputDate = new Date(date);

        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setUTCDate(now.getUTCDate() - 7);

        // ✅ Obtener predicción solo para partidos futuros (>= now) en modo predict
        if (inputDate >= sevenDaysAgo && inputDate >= now && mode === 'predict') {
            const url = URL_FACTORY.ObtenerPrediccionDelTiempo(id);
            axiosApiInstance.get(url).then((res) => {
                setPrediction(res.data);
            });
        }
    }, [id, date, league, mode]);

    // Fetch eventos y estadísticas del partido
    useEffect(() => {
        if (!id || !date) return;

        const now = new Date();
        const inputDate = new Date(date);

        if (inputDate < now && id) {
            const url = URL_FACTORY.ObtenerPartidoFixtureEvents(id);
            axiosApiInstance.get(url).then((res) => {
                const fixtureEvents = res.data?.length > 0 ? mapToEvents(res.data) : [];
                setEvents(fixtureEvents);
            });

            const homeId = home?.id ?? 0;
            const awayId = away?.id ?? 0;

            if (league > 0 && season > 0 && (homeId > 0 || awayId > 0)) {
                const payload = {
                    homeId: homeId > 0 ? homeId : null,
                    awayId: awayId > 0 ? awayId : null,
                    league,
                    season,
                };

                const url = URL_FACTORY.ObtenerPartidoEstadisticas;

                axiosApiInstance
                    .post(url, payload)
                    .then((res) => {
                        const statsMapped = mapToStats(res.data?.home, res.data?.away);
                        setStats({
                            ...statsMapped,
                            season: currentSeasonStats,
                        });
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            }
        }
    }, [id, date, league, season, home?.id, away?.id]);

    // Fetch eventos para modo 'last'
    useEffect(() => {
        if (!data || mode !== 'last') return;

        const homeFixtureId = home?.lastFixture?.id;
        const awayFixtureId = away?.lastFixture?.id;

        if (homeFixtureId && awayFixtureId) {
            const url = URL_FACTORY.ObtenerPartidoFixtureEvents(homeFixtureId, awayFixtureId);
            axiosApiInstance.get(url).then((res) => {
                if (res.data?.length > 0) {
                    setEvents(mapToEvents(res.data));
                } else {
                    setEvents([]);
                }
            });
        }
    }, [data, mode, home?.lastFixture?.id, away?.lastFixture?.id]);

    // Fetch estadísticas del partido
    useEffect(() => {
        let idsPartido = [];
        let inputDate = new Date(date);

        if (mode === 'last') {
            idsPartido = [home?.lastFixture?.id, away?.lastFixture?.id];
            inputDate = new Date(home?.lastFixture?.date ?? away?.lastFixture?.date);
        } else {
            idsPartido = [id];
        }

        if (!idsPartido || idsPartido.length === 0 || !inputDate) return;

        const now = new Date();

        if (inputDate < now) {
            const url = URL_FACTORY.ObtenerPartidoEstadisticas;

            const homePlayers = home?.lineup?.filter((player) => player.id).map((player) => player.id) ?? [];
            const awayPlayers = away?.lineup?.filter((player) => player.id).map((player) => player.id) ?? [];

            const players = [...homePlayers, ...awayPlayers];

            if (players.length > 0) {
                const payload = {
                    ids: idsPartido,
                    mode: confirmedFantasy,
                    subMode: confirmedFantasyMode,
                    players,
                };
                axiosApiInstance.post(url, payload).then((res) => {
                    setEstadisticasPartido(res.data?.estadisticas);
                    addEstadisticasPartido(res.data?.estadisticas);
                });
            }
        }
    }, [
        id,
        date,
        mode,
        home?.lastFixture?.id,
        home?.lastFixture?.date,
        away?.lastFixture?.id,
        away?.lastFixture?.date,
        home?.lineup,
        away?.lineup,
        confirmedFantasy,
        confirmedFantasyMode,
    ]);

    // Actualizar estadísticas seleccionadas según fantasy mode
    useEffect(() => {
        if (confirmedFantasy && confirmedFantasyMode && estadisticasPartido) {
            const estadisticasSeleccionadas = estadisticasPartido?.find(
                (estadistica) =>
                    estadistica.mode === confirmedFantasy && estadistica.subMode === confirmedFantasyMode
            );

            setEstadisticasPartidoSeleccionada(estadisticasSeleccionadas);
        }
    }, [confirmedFantasy, confirmedFantasyMode, estadisticasPartido]);

    // Fetch editor lineups (para ambos equipos del partido)
    useEffect(() => {
        if (!id) return;

        const url = URL_FACTORY.ObtenerAlineacionesEditores;
        const payload = {
            fixtureId: id,
            // No pasamos teamId para obtener alineaciones de ambos equipos
        };

        axiosApiInstance
            .post(url, payload)
            .then((res) => {
                setEditorLineups(res.data);
            })
            .catch((err) => {
                console.error('Error fetching editor lineups:', err);
            });
    }, [id]);

    // Calcular fantasyEnabled
    const fantasyEnabled = useMemo(() => {
        return {
            isBiwengerEnabled: estadisticasPartido?.find(
                (estadistica) =>
                    estadistica.mode === FANTASY.BIWENGER &&
                    estadistica.estadisticas &&
                    Object.keys(estadistica.estadisticas).length > 0
            ),
            isFantasyLaLigaEnabled: estadisticasPartido?.find(
                (estadistica) =>
                    estadistica.mode === FANTASY.FANTASY &&
                    estadistica.estadisticas &&
                    estadistica.estadisticas
            ),
            isAnaliticaFantasyEnabled: estadisticasPartido?.find(
                (estadistica) => estadistica.mode === FANTASY.ANALITICA && estadistica.estadisticas
            ),
            isFantasyMarcaEnabled: estadisticasPartido?.find(
                (estadistica) => estadistica.mode === FANTASY.FANTASY_MARCA && estadistica.estadisticas
            ),
            isMisterFantasyEnabled: estadisticasPartido?.find(
                (estadistica) => estadistica.mode === FANTASY.MISTER_FANTASY && estadistica.estadisticas
            ),
        };
    }, [estadisticasPartido]);

    const value = {
        // Data original
        data,

        // Estados de fetching
        h2h,
        events,
        estadisticasPartido: estadisticasPartidoSeleccionada,
        stats,
        handleOpenJugador,
        fantasyEnabled,
        editorLineups,
        prediccionTiempo: prediction, // ✅ Renombrado para consistencia con EquipoAlineacionesContext

        // Estado del variant y editor seleccionado
        variant,
        setVariant,
        selectedEditorLineup,
        setSelectedEditorLineup,

        // Datos derivados
        mode,
        home,
        away,
    };

    return (
        <PartidoAlineacionesContext.Provider value={value}>{children}</PartidoAlineacionesContext.Provider>
    );
};
